import React, { useEffect, useRef, useState } from "react";
import { Table, Select, Input } from "antd";
import type { TableColumnsType } from "antd";
import { ChevronDown, ChevronUp, Filter } from "react-feather";
import { SearchOutlined } from "@ant-design/icons";

import CustomPagination from "../../shared/CustomPagination/CustomPagination";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatTimezoneDisplay } from "../../../utils/dateService";
import { useGetStaff } from "../../../queries/useGetStaff";
import {
  useGetStaffRolesDropdown,
  useGetStaffStatusDropdown,
} from "../../../queries/useGetStaffDropdowns";
import type { StaffRecord, StaffRole, StaffStatus } from "../../../types/staff";
import StaffDetails from "./StaffDetails/StaffDetails";
import { useProductFeatureFlag } from "../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../utils/products";

import "./stylesStaff.css";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";

/** Shown in the Role filter dropdown before the API responds. */
const DEFAULT_ROLE_OPTIONS: Array<{ value: string; label: string; color?: string }> = [];

/** Shown in the Status filter dropdown before the API responds. */
const DEFAULT_STATUS_OPTIONS: Array<{ value: string; label: string }> = [];

/** Stable empty array so we don't create a new reference on every render. */
const EMPTY_STAFF_ITEMS: StaffRecord[] = [];

interface StaffProps {
  onDetailsOpenChange?: (isOpen: boolean) => void;
  /**
   * Deep link: when this id matches a row in the currently loaded staff list
   * (`id` or `admin_id`), open that member’s details. If they are on another page
   * of results, nothing happens until the list API supports fetch-by-id.
   */
  initialStaffId?: string | null;
}

/**
 * Main staff management table. Fetches paginated PAM accounts from the backend
 * and lets the user search and filter by role/status. Clicking a first name
 * navigates to the staff detail view.
 */
function Staff({ onDetailsOpenChange, initialStaffId = null }: StaffProps) {
  const { isEnabled: isPam2faEnabled } = useProductFeatureFlag(
    "pam_2fa_enabled",
    TRIVELTA_PRODUCTS.PAM,
  );
  const [showExtraFilters, setShowExtraFilters] = useState(false);

  // "Pending" filters live in the dropdown UI; they only become "active"
  // (and hit the API) when the user clicks Apply.
  const [pendingRoleFilters, setPendingRoleFilters] = useState<StaffRole[]>([]);
  const [pendingStatusFilters, setPendingStatusFilters] = useState<
    StaffStatus[]
  >(['active', 'unverified']);

  const [activeRoleFilters, setActiveRoleFilters] = useState<StaffRole[]>([]);
  const [activeStatusFilters, setActiveStatusFilters] = useState<StaffStatus[]>(
    ['active', 'unverified'],
  );

  // searchInputValue updates on every keystroke, searchValue is the debounced
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [isFindShortcutHighlightVisible, setIsFindShortcutHighlightVisible] =
    useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  /** Cursor for each page: cursorStack[0] = undefined (page 1), cursorStack[1] = key for page 2, etc. */
  const [cursorStack, setCursorStack] = useState<(string | null | undefined)[]>([
    undefined,
  ]);

  const [selectedStaff, setSelectedStaff] = useState<StaffRecord | null>(null);
  const findHighlightTimeoutRef = useRef<number>(0);
  const lastOpenedDeepLinkStaffIdRef = useRef<string | null>(null);

  const { data: rolesDropdown } = useGetStaffRolesDropdown();
  const { data: statusDropdown } = useGetStaffStatusDropdown();
  const roleOptions = rolesDropdown?.length ? rolesDropdown : DEFAULT_ROLE_OPTIONS;
  const statusOptions =
    statusDropdown?.length ? statusDropdown : DEFAULT_STATUS_OPTIONS;

  // Create lookup map for roles: roleId -> { label, color }
  const rolesLookup = React.useMemo(() => {
    const map = new Map<string, { label: string; color?: string }>();
    roleOptions.forEach((opt) => {
      map.set(opt.value, { label: opt.label, color: opt.color });
    });
    return map;
  }, [roleOptions]);

  const lastEvaluatedKey =
    currentPage <= 1 ? undefined : cursorStack[currentPage - 1] ?? undefined;

  const { data: staffResponse, isLoading } = useGetStaff({
    last_evaluated_key: lastEvaluatedKey,
    page_size: pageSize,
    page: currentPage,
    search: searchValue || undefined,
    roles: activeRoleFilters.length ? activeRoleFilters : undefined,
    statuses: activeStatusFilters.length ? activeStatusFilters : undefined,
  });

  // Enrich staff items with role name/color from dropdown lookup
  const staffItems = React.useMemo(() => {
    const items = staffResponse?.items ?? EMPTY_STAFF_ITEMS;
    return items.map((item) => {
      // If role already has value (legacy format), use it; otherwise lookup from dropdown
      if (item.role) {
        return item;
      }
      const roleData = rolesLookup.get(item.roleId);
      return {
        ...item,
        role: roleData?.label ?? item.roleId,
        roleColor: roleData?.color ?? "",
      };
    });
  }, [staffResponse?.items, rolesLookup]);
  const totalItems = staffResponse?.pagination?.total_items ?? 0;

  useEffect(() => {
    const target = initialStaffId?.trim();
    if (!target || isLoading) {
      return;
    }
    if (lastOpenedDeepLinkStaffIdRef.current === target) {
      return;
    }
    const match = staffItems.find(
      (s) => s.id === target || s.admin_id === target,
    );
    if (match) {
      setSelectedStaff(match);
      onDetailsOpenChange?.(true);
      lastOpenedDeepLinkStaffIdRef.current = target;
    }
  }, [initialStaffId, isLoading, staffItems, onDetailsOpenChange]);

  /** Store cursor for next page when we receive a response. */
  useEffect(() => {
    if (staffResponse == null) return;
    const key = staffResponse.last_evaluated_key ?? null;
    setCursorStack((prev) => {
      const next = [...prev];
      while (next.length <= currentPage) next.push(undefined);
      next[currentPage] = key;
      return next;
    });
  }, [currentPage, staffResponse]);

  /** Reset cursor stack when search or filters change (we go back to page 1). */
  useEffect(() => {
    setCursorStack([undefined]);
  }, [searchValue, activeRoleFilters, activeStatusFilters, pageSize]);

  /** Debounce the search input so we don't fire an API call on every keystroke. */
  useEffect(() => {
    if (searchInputValue === searchValue) {
      return undefined;
    }

    const debounceTimeout = window.setTimeout(() => {
      setSearchValue(searchInputValue);
      setCurrentPage(1);
    }, 1000);

    return () => window.clearTimeout(debounceTimeout);
  }, [searchInputValue, searchValue]);

  /**
   * When the user hits Cmd/Ctrl+F, briefly highlight the search bar with a
   * green border so new Trivelta clients learn to use it instead of the
   * browser's native find.
   */
  useEffect(() => {
    const handleFindShortcut = (event: KeyboardEvent) => {
      const isFindShortcutPressed =
        event.key.toLowerCase() === "f" && (event.metaKey || event.ctrlKey);

      if (!isFindShortcutPressed) {
        return;
      }

      window.clearTimeout(findHighlightTimeoutRef.current);
      setIsFindShortcutHighlightVisible(true);

      findHighlightTimeoutRef.current = window.setTimeout(() => {
        setIsFindShortcutHighlightVisible(false);
      }, 2000);
    };

    window.addEventListener("keydown", handleFindShortcut);

    return () => {
      window.clearTimeout(findHighlightTimeoutRef.current);
      window.removeEventListener("keydown", handleFindShortcut);
    };
  }, []);

  /** Wipe every filter (pending + active) and reset the search + pagination. */
  const handleClearFilters = () => {
    setPendingRoleFilters([]);
    setPendingStatusFilters([]);

    setActiveRoleFilters([]);
    setActiveStatusFilters([]);

    setSearchInputValue("");
    setSearchValue("");
    setCurrentPage(1);
  };

  /** Promote the pending dropdown selections to active filters and refetch. */
  const handleApply = () => {
    setActiveRoleFilters(pendingRoleFilters);
    setActiveStatusFilters(pendingStatusFilters);
    setCurrentPage(1);
  };

  const columns: TableColumnsType<StaffRecord> = [
    {
      title: AppStrings.FIRST_NAME,
      dataIndex: "firstName",
      key: "firstName",
      width: 120,
      render: (text: StaffRecord["firstName"], record) => (
        <span
          className="staff-hoverable"
          onClick={() => {
            setSelectedStaff(record);
            if (onDetailsOpenChange) {
              onDetailsOpenChange(true);
            }
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: AppStrings.LAST_NAME,
      dataIndex: "lastName",
      key: "lastName",
      width: 120,
      render: (text: StaffRecord["lastName"], record) => (
        <span
          className="staff-hoverable"
          onClick={() => {
            setSelectedStaff(record);
            if (onDetailsOpenChange) onDetailsOpenChange(true);
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: AppStrings.EMAIL,
      dataIndex: "email",
      key: "email",
      width: 250,
      render: (text: StaffRecord["email"], record) => (
        <span
          className="staff-hoverable"
          onClick={() => {
            setSelectedStaff(record);
            if (onDetailsOpenChange) onDetailsOpenChange(true);
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Timezone",
      dataIndex: "timezone",
      key: "timezone",
      width: 300,
      render: (text: StaffRecord["timezone"]) => (
        <span>{text ? formatTimezoneDisplay(text) : "-"}</span>
      ),
    },
    {
      title: AppStrings.ROLE,
      dataIndex: "role",
      key: "role",
      width: 200,
      render: (text: StaffRecord["role"], record) => (
        <div className="staff-role-cell">
          {record.roleColor ? (
            <span
              className="role-dot"
              style={{ backgroundColor: record.roleColor }}
            />
          ) : null}
          <span>{text ? String(text).replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "-"}</span>
        </div>
      ),
    },
    {
      title: AppStrings.STATUS,
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (text: StaffRecord["status"], record) => (
        <span
          className="staff-status-badge"
          style={{ backgroundColor: record.statusColor }}
        >
          {text ? String(text).replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "-"}
        </span>
      ),
    },
    ...(isPam2faEnabled
      ? [
          {
            title: AppStrings.PAM_STAFF_2FA_STATUS,
            dataIndex: "mfaEnrolled" as const,
            key: "mfaEnrolled",
            width: 150,
            render: (mfaEnrolled: StaffRecord["mfaEnrolled"]) => (
              <span
                className={`staff-mfa-status-badge ${
                  mfaEnrolled === true
                    ? "staff-mfa-status-badge--enrolled"
                    : mfaEnrolled === false
                      ? "staff-mfa-status-badge--not-enrolled"
                      : ""
                }`}
              >
                {mfaEnrolled === true
                  ? AppStrings.PAM_STAFF_2FA_ENROLLED
                  : mfaEnrolled === false
                    ? AppStrings.PAM_STAFF_2FA_NOT_ENROLLED
                    : "-"}
              </span>
            ),
          },
        ]
      : []),
  ];

  if (selectedStaff) {
    return (
      <StaffDetails
        staff={selectedStaff}
        isPam2faEnabled={isPam2faEnabled}
        onBack={() => {
          setSelectedStaff(null);
          if (onDetailsOpenChange) {
            onDetailsOpenChange(false);
          }
        }}
      />
    );
  }

  return (
    <div className="staff-container">
      <div className="staff-content-header">
        <div className="staff-autocomplete-container">
          <div
            className={`staff-search-wrapper ${isFindShortcutHighlightVisible
                ? "staff-search-wrapper-highlight"
                : ""
              }`}
            data-testid="staff-search-wrapper"
          >
            <Input
              placeholder="Search"
              prefix={
                <SearchOutlined
                  style={{
                    fontSize: "18px",
                    color: "var(--COLOR-LIGHT_TEXT_COLOR)",
                  }}
                />
              }
              value={searchInputValue}
              onChange={(event) => setSearchInputValue(event.target.value)}
              style={{
                background: "transparent",
                border: "none",
              }}
              className="staff-search-input"
            />
          </div>
          <div className="divider" />
          <div
            className="filter-buttons"
            onClick={() => setShowExtraFilters(!showExtraFilters)}
            data-testid="staff-filter-toggle"
          >
            <Filter className="filter-icon" />
            {showExtraFilters ? (
              <ChevronUp className="filter-icon" />
            ) : (
              <ChevronDown className="filter-icon" />
            )}
          </div>
        </div>
      </div>

      <div
        className="staff-selectors"
        style={{ display: showExtraFilters ? "flex" : "none" }}
        data-testid="staff-filter-selectors"
      >
        <div className="staff-selector">
          <span>{AppStrings.ROLE}</span>
          <Select
            mode="multiple"
            placeholder={AppStrings.NOT_SELECTED}
            onChange={(value) => setPendingRoleFilters(value as StaffRole[])}
            value={pendingRoleFilters}
            options={roleOptions}
            virtual={false}
            optionRender={(option) => (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {option.data.color && (
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: option.data.color,
                      flexShrink: 0,
                    }}
                  />
                )}
                <span>{option.label}</span>
              </div>
            )}
          />
        </div>

        <div className="staff-selector">
          <span>{AppStrings.STATUS}</span>
          <Select
            mode="multiple"
            placeholder={AppStrings.NOT_SELECTED}
            onChange={(value) =>
              setPendingStatusFilters(value as StaffStatus[])
            }
            value={pendingStatusFilters}
            options={statusOptions}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            virtual={false}
          />
        </div>
      </div>

      <div
        className="staff-selector-action-buttons"
        style={{ display: showExtraFilters ? "flex" : "none" }}
        data-testid="staff-filter-actions"
      >
        <button className="clear" onClick={handleClearFilters}>
          {AppStrings.CLEAR_FILTERS}
        </button>
        <button
          className={`apply ${pendingRoleFilters.length > 0 || pendingStatusFilters.length > 0 ? "apply--active" : ""}`}
          onClick={handleApply}
          disabled={
            pendingRoleFilters.length === 0 && pendingStatusFilters.length === 0
          }
        >
          {AppStrings.APPLY}
        </button>
      </div>

      {isLoading ? (
        <div className="staff-table-loading" data-testid="staff-table-loading">
          <RebetLoader width={100} height={100} />
        </div>
      ) : (
        <div className="staff-table-container" data-testid="staff-table-container">
          <Table
            columns={columns}
            dataSource={staffItems}
            pagination={false}
            rowKey="id"
            scroll={{ x: 1050, y: 500 }}
            locale={{ emptyText: "No data" }}
            rowClassName={(record) => 
              record.status?.toLowerCase() === "deleted" ? "staff-row-deleted" : ""
            }
          />
          <CustomPagination
            total={totalItems}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageSizeChange={(value: number) => {
              setCurrentPage(1);
              setPageSize(value);
            }}
            onPageChange={(page: number) => setCurrentPage(page)}
            itemLabel="staff"
          />
        </div>
      )}
    </div>
  );
}

export default Staff;
